import React, { useContext, useEffect, useState } from 'react'
import { captureGAEvent, convertToLocalTime, getImage } from '../../utils/helper'
import { AppContext } from '../../context/app'
import { isEmpty } from 'lodash'
import useAxios from 'axios-hooks'
import { ANALYTICS_API, PLAYER_SESSION } from '../../utils/apiUrls'
import { LG_SIZE, SM_SIZE } from '../../utils/constants'
import CustomVideoJs from '../../components/player/customVideoJs'
import { isIOS, isMobile } from 'react-device-detect'
import { ContentContext } from '../../context/content'
import moment from 'moment'

const CustomPlayer = (props) => {
  let PLAYER_STARTED = false
  const {
    playerData,
    contentInfo,
    contentWatchUrl,
    resumeTime,
    handlePlayingSequence,
    disableHeader = false,
    loop = false,
    controlBar = true,
    adCampaignData,
    onVideoComplete,
    handleNext = null,
    handlePrev = null,
    stillWatchingAllowed = false,
    isMp4 = false,
  } = props
  const [error, setError] = useState(false)
  const playerRef = React.useRef(null)
  const { accountObj, userObj, isLogin } = useContext(AppContext)
  const { updateContentResumeTimestamp } = useContext(ContentContext)
  const [source, setSource] = useState('WEB')
  const randomVal = Math.floor(1000 + Math.random() * 9000)
  const trackingId = `${contentInfo?.content_id + Date.now() + randomVal}`

  const sessionLimit = accountObj?.od_resource_allowances?.session_limit

  const isIphone = isMobile && isIOS

  const handleResize = () => {
    if (window.innerWidth > LG_SIZE) {
      setSource('WEB')
    } else if (window.innerWidth < LG_SIZE && window.innerWidth > SM_SIZE) {
      setSource('TABLET')
    } else {
      setSource('MOBILE')
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const [{ error: analyticsError }, analyticsEventCall] = useAxios(
    {
      url: ANALYTICS_API,
      method: 'post',
    },
    { manual: true, autoCancel: false },
  )

  const [{ error: sessionError }, sessionEventCall] = useAxios(
    {
      url: PLAYER_SESSION,
      method: 'post',
    },
    { manual: true, autoCancel: false },
  )

  useEffect(() => {
    console.error(sessionError)
  }, [sessionError])

  useEffect(() => {
    console.error(analyticsError)
  }, [analyticsError])

  useEffect(() => {
    if (error) {
      handleError()
    }
  }, [error])

  const getSourceType = () => {
    if (contentInfo?.asset_type === 'AUDIO') {
      return `audio/mp3`
    }

    return 'application/x-mpegURL'
  }

  const videoJsOptions = {
    // lookup the options in the docs for more options
    autoplay: !isIphone,
    playbackRates: [0.5, 0.75, 1, 1.25, 1.5],
    responsive: true,
    liveui: true,
    controls: contentInfo?.video_type === 'LIVE',
    poster: getImage(accountObj?.cf_domain_name, contentInfo?.poster),
    // fluid: true,
    userActions: {
      hotkeys: true,
    },
    fill: true,
    loop: loop,
    sources: [
      {
        // src: 'https://cdn.bitmovin.com/content/assets/sintel/hls/playlist.m3u8',
        src: contentWatchUrl,
        type: isMp4 ? 'video/mp4' : getSourceType(),
      },
    ],
    plugins: {
      qualityLevels: {},
    },
    techOrder: ['html5'],
    html5: {
      hls: {
        overrideNative: true,
        cacheEncryptionKeys: true,
      },
    },
    nativeControlsForTouch: false,
    inactivityTimeout: 2000,
  }

  const handleError = () => {
    if (!playerRef.current) {
      return
    }
    // playerRef.current.suppressNotSupportedError(true);
  }

  useEffect(() => {
    if (playerRef.current) {
      playVideo(contentWatchUrl)
    }
  }, [contentWatchUrl])

  const playVideo = (url) => {
    let videoElm = playerRef.current

    if (!videoElm.paused) {
      videoElm.pause()
    }
    videoElm.src({
      type: isMp4 ? 'video/mp4' : getSourceType(),
      src: url,
      // src: 'https://cdn.bitmovin.com/content/assets/sintel/hls/playlist.m3u8',
    })

    videoElm.load()
    videoElm.play()
  }

  const handlePlayerReady = (player) => {
    playerRef.current = player
    let timeInterval
    let previousTime = 0
    let currentTime = 0
    let position = 0
    // you can handle player events here
    player.on('waiting', () => {
      console.log('player is waiting')
    })

    player.on('ready', () => {
      player.playsinline(true)
    })

    player.on('loadedmetadata', async () => {
      if (resumeTime > 0) {
        player.currentTime(resumeTime)
      }
      if (contentInfo?.video_type === 'VOD_LIVE') {
        let duration = moment.duration(
          moment(new Date(), 'HH:mm').diff(
            moment(convertToLocalTime(contentInfo.publish_time), 'HH:mm'),
          ),
        )

        player.currentTime(duration.asSeconds())
      }

      captureGAEvent('player', 'player', 'Player', {
        user_id: userObj?._id || '',
        user_email: userObj?.email_id || '',
        content_name: contentInfo.title,
        content_id: contentInfo.content_id,
        type: 'PLAYER_PLAY',
      })
      await analyticsEventCall({
        data: {
          event_type: 'PLAYER',
          event_name: 'PLAYER_START',
          content_id: contentInfo?.content_id,
          player_timestamp: `0`,
          total_duration: `${player.duration()}`,
          tracking_id: trackingId,
          source: source,
          variant: `${player.videoHeight()}`,
        },
      })

      let playerStartedRes = await analyticsEventCall({
        data: {
          event_type: 'PLAYER',
          event_name: 'PLAYER_PLAY',
          content_id: contentInfo?.content_id,
          player_timestamp: `0`,
          total_duration: `${player.duration()}`,
          tracking_id: trackingId,
          source: source,
          variant: `${player.videoHeight()}`,
        },
      })

      playerStartedRes?.data?.success ? (PLAYER_STARTED = true) : (PLAYER_STARTED = false)

      timeInterval = setInterval(async () => {
        if (PLAYER_STARTED) {
          updateContentResumeTimestamp(contentInfo?.content_id, player.currentTime())
          await analyticsEventCall({
            data: {
              event_type: 'PLAYER',
              event_name: 'PLAYER_PING',
              content_id: contentInfo?.content_id,
              player_timestamp: `${player.currentTime()}`,
              total_duration: `${player.duration()}`,
              tracking_id: trackingId,
              source: source,
              variant: `${player.videoHeight()}`,
            },
          })
          if (isLogin && sessionLimit) {
            await sessionEventCall({
              data: {
                event_type: 'UPDATE',
                content_id: contentInfo?.content_id,
                session_limit: sessionLimit,
              },
            })
          }
        }
      }, 30000)
    })

    player.on('play', async () => {
      if (PLAYER_STARTED && !player.seeking()) {
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_PLAY',
            content_id: contentInfo?.content_id,
            player_timestamp: `${player.currentTime()}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
      }
    })

    player.on('pause', async () => {
      if (PLAYER_STARTED && !player.seeking() && player.currentTime() < player.duration()) {
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_PAUSE',
            content_id: contentInfo?.content_id,
            player_timestamp: `${player.currentTime()}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
        captureGAEvent('player', 'player', 'Player', {
          user_id: userObj?._id || '',
          user_email: userObj?.email_id || '',
          content_name: contentInfo.title,
          content_id: contentInfo.content_id,
          type: 'PLAYER_PAUSE',
        })
      }
      // clearInterval(timeInterval)
      // console.log('player is paused', player.textTracks())
    })

    player.on('timeupdate', function () {
      previousTime = currentTime
      currentTime = Math.floor(player.currentTime())

      // save 'position' so long as time is moving forward with each update
      if (previousTime < currentTime) {
        position = previousTime
        previousTime = currentTime
      }
    })

    player.on('seeked', async () => {
      if (PLAYER_STARTED) {
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_PAUSE',
            content_id: contentInfo?.content_id,
            player_timestamp: `${position}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })

        if (!player.paused()) {
          await analyticsEventCall({
            data: {
              event_type: 'PLAYER',
              event_name: 'PLAYER_PLAY',
              content_id: contentInfo?.content_id,
              player_timestamp: `${player.currentTime()}`,
              total_duration: `${player.duration()}`,
              tracking_id: trackingId,
              source: source,
              variant: `${player.videoHeight()}`,
            },
          })
        }
      }
    })

    player.on('dispose', async () => {
      if (PLAYER_STARTED) {
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_CLOSE',
            content_id: contentInfo?.content_id,
            player_timestamp: `${player.currentTime()}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
        if (isLogin && sessionLimit) {
          await sessionEventCall({
            data: {
              event_type: 'DELETE',
            },
          })
        }
      }

      clearInterval(timeInterval)
    })

    player.on('ended', async () => {
      if (PLAYER_STARTED) {
        handlePlayingSequence()
        onVideoComplete()
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_END',
            content_id: contentInfo?.content_id,
            player_timestamp: `${player.currentTime()}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
      }
      clearInterval(timeInterval)
    })

    player.on('error', () => {
      // console.log(player.error());
      // let error = player.error()
      // raiseToast('error', error.message)
      setError(true)
    })
  }
  return (
    <div className="flex flex-col xl:flex-row w-full h-full">
      <div style={{ maxWidth: '100%' }} className={`player-container w-full h-full relative`}>
        {!disableHeader && (
          <div
            style={{
              zIndex: 40,
              backgroundColor: 'rgb(27, 36, 47,0.30)',
            }}
            className={` overlay grid grid-cols-12 items-center md:px-4 h-10 md:h-14 w-full`}
          >
            <div className="text-white col-span-2">
              {!isEmpty(contentInfo?.content_rating) ? (
                <div className="mx-2">
                  <div className="flex items-center justify-start">
                    {contentInfo?.content_rating?.icon?.s3_key && (
                      <div className="mr-1">
                        <img
                          src={getImage(
                            accountObj?.cf_domain_name,
                            contentInfo?.content_rating?.icon?.s3_key,
                          )}
                          className="w-4 h-4 md:w-7 md:h-7 mx-1"
                          alt="Rating"
                        />
                      </div>
                    )}
                    <p className="text-xs md:text-sm font-semibold line-clamp-1">
                      {contentInfo?.content_rating?.name}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="text-white font-semibold text-sm md:text-lg col-span-8 text-center">
              {contentInfo?.title}
            </div>
          </div>
        )}

        <CustomVideoJs
          contentInfo={contentInfo}
          options={videoJsOptions}
          onReady={handlePlayerReady}
          controlBar={contentInfo?.video_type === 'LIVE' ? false : controlBar}
          isLive={contentInfo?.video_type === 'LIVE'}
          isVODLive={contentInfo?.video_type === 'VOD_LIVE'}
          adSetData={adCampaignData?.ad_sets ? adCampaignData?.ad_sets : []}
          handleNext={handleNext}
          handlePrev={handlePrev}
          stillWatchingAllowed={stillWatchingAllowed}
          playerData={playerData}
          isAudio={contentInfo?.asset_type === 'AUDIO'}
        />
      </div>
    </div>
  )
}

export default CustomPlayer
